import { styled } from '@sds-system/system';
import type { CSSObject } from '@sds-system/types';
import { getAllStyledProps, processNestedCSS } from '@sds-system/utils';
import type {
  StyledTextFieldInputProps,
  StyledTextFieldWrapperProps,
  StyledTextFiledRootProps,
  TextFieldSizeKey,
} from './TextField.types';

const sizeStyle: Record<TextFieldSizeKey, CSSObject> = {
  L: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  M: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  XL: {
    paddingTop: 16,
    paddingBottom: 16,
  },
};

export const StyledTextFieldRoot = styled('div', {
  componentName: 'TextField',
  slot: 'root',
})<StyledTextFiledRootProps>(() => {
  return {
    width: '100%',
  };
}, getAllStyledProps);

export const StyledTextFieldWrapper = styled('div', {
  componentName: 'TextField',
  slot: 'wrapper',
})<StyledTextFieldWrapperProps>(
  ({ size, theme, disabled, isError, isSuccess }) => {
    return {
      ...theme.typography.body2.regular,
      ...sizeStyle[size],
      color: theme.palette.text.primary,
      width: '100%',
      height: theme.size[size],
      paddingRight: 16,
      paddingLeft: 16,
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      border: `1px solid ${theme.palette.border.secondary}`,
      borderRadius: theme.borderRadius[8],
      backgroundColor: theme.palette.bg.primary,

      ...(disabled && {
        borderColor: theme.palette.border.disabled,
        backgroundColor: theme.palette.bg.disabledLight,
      }),
      ...(isSuccess && {
        borderColor: theme.palette.border.success,
      }),
      ':focus-within': processNestedCSS(
        !isError
          ? {
              borderColor: theme.palette.border.interactive.primary,
            }
          : {},
        theme,
      ),
      ...(isError && {
        borderColor: theme.palette.border.error,
      }),
    };
  },
  ({ theme }) => ({
    variants: [
      {
        props: { variant: 'searchBar' },
        style: {
          borderRadius: theme.borderRadius[999],
          paddingRight: 8,
        },
      },
      {
        props: { variant: 'underline' },
        style: {
          borderTop: theme.borderSize[0],
          borderLeft: theme.borderSize[0],
          borderRight: theme.borderSize[0],
          borderRadius: theme.borderRadius[0],
          padding: '0px 4px',
        },
      },
      {
        props: { size: 'XL' },
        style: {
          '::placeholder': {
            ...theme.typography.body.regular,
          },
          ...theme.typography.body.regular,
        },
      },
    ],
  }),
  getAllStyledProps,
);

export const StyledTextFieldInput = styled('input', {
  componentName: 'TextField',
  slot: 'input',
})<StyledTextFieldInputProps>(({ theme }) => {
  return {
    width: '100%',
    height: '100%',
    padding: 0,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 'inherit',
    '::-ms-reveal': {
      display: 'none',
    },
    '::-ms-clear': {
      display: 'none',
    },
    '::placeholder': processNestedCSS(
      {
        color: theme.palette.text.assistance,
        ...theme.typography.body2.regular,
      },
      theme,
    ),
    ':disabled': processNestedCSS(
      {
        color: theme.palette.text.secondary,
        '::placeholder': processNestedCSS(
          {
            color: theme.palette.text.disabled,
          },
          theme,
        ),
      },
      theme,
    ),
  };
}, getAllStyledProps);
